import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Alert, Box, Typography } from "@mui/material";
import { Home } from "@mui/icons-material";
import AuthenticatingPage from "./actprep-ui/pages/AuthenticatingPage";
import ErrorPage from "./actprep-ui/pages/ErrorPage";
import SkeletonLayout from "./actprep-ui/components/SkeletonLayout/SkeletonLayout";
import AppLayout from "./actprep-ui/components/AppLayout";
import { useProfile } from "./actprep-ui/context/ProfileContext";
import {
  ImpersonationProvider,
  useImpersonation,
} from "./actprep-ui/context/ImpersonationContext";
import { urls } from "./actprep-ui/utils/urls";
import Redirect from "./actprep-ui/components/Redirect";
import { groups } from "./actprep-ui/constants/auth";
import ImpersonationBanner from "./actprep-ui/components/ImpersonationBanner";
import HomeRouter from "./pages/Home";
import ImpersonateParent from "./pages/ImpersonateParent";
import { lazy, Suspense } from "react";

const InstallPage = lazy(() => import("./pages/Install"));

function SelectParentMessage() {
  return (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        py: 4,
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "640px" }}>
        <Typography variant="h5" color="primary" fontWeight="bold" mb={2}>
          Dashboard
        </Typography>
        <Alert severity="info">
          Select a parent to impersonate before viewing dashboard data.
        </Alert>
      </Box>
    </Box>
  );
}

function ParentRoutes({ isEmployee, isParent, profile }) {
  const {
    impersonatedParent,
    isImpersonating,
    isLoading: isImpersonationLoading,
  } = useImpersonation();

  if (isEmployee && isImpersonationLoading) {
    return <SkeletonLayout />;
  }

  const selfParentUuid = profile?.parent?.uuid;
  const selfParentLabel =
    `${profile?.user?.first_name || ""} ${profile?.user?.last_name || ""}`.trim() ||
    profile?.user?.email ||
    "Unknown Parent";
  const selfParentProfile = selfParentUuid
    ? {
        uuid: selfParentUuid,
        label: selfParentLabel,
      }
    : null;
  const targetParentProfile = selfParentProfile || (isEmployee ? impersonatedParent : null);
  const requiresParentSelection =
    (isEmployee && !selfParentProfile?.uuid && !isImpersonating) ||
    (isParent && !targetParentProfile?.uuid);
  const navLinkSections = [
    {
      section: "Home",
      icon: <Home />,
      path: "/dashboard/",
      links: [{ label: "Dashboard", path: "/dashboard/" }],
    },
  ];

  return (
    <AppLayout
      navlinkSections={navLinkSections}
      appTitle="Parent"
      injectedComponents={<ImpersonationBanner />}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard/" />} />
        <Route
          path="/dashboard/*"
          element={
            requiresParentSelection ? (
              <SelectParentMessage />
            ) : (
              <HomeRouter
                targetParentProfile={targetParentProfile}
                showViewingParentLabel={isEmployee && isImpersonating}
              />
            )
          }
        />
        <Route path="*" element={<Redirect to={"/error/404/"} />} />
      </Routes>
    </AppLayout>
  );
}

function AppContent() {
  const { profile, isLoaded: isProfileLoaded } = useProfile();
  const isAuthCallbackUrl = window.location.href.includes(urls.sso.redirect());
  const isInstallRoute = window.location.pathname.startsWith("/install");
  const isParent = profile?.groups?.includes(groups.PARENTS);
  const isEmployee = profile?.groups?.includes(groups.EMPLOYEES);
  const hasAccess = isParent || isEmployee;

  if (!(isInstallRoute || isProfileLoaded || isAuthCallbackUrl)) {
    return <SkeletonLayout />;
  }

  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <Routes>
        <Route path="/error/:errorCode/" element={<ErrorPage />} />
        <Route
          path="/oauth/callback/"
          element={<AuthenticatingPage authenticate />}
        />
        <Route
          path="/impersonate/:parentUuid/"
          element={
            isEmployee ? (
              <ImpersonateParent />
            ) : (
              <Navigate to="/error/403/" replace />
            )
          }
        />
        <Route
          path="/install/*"
          element={
            <Suspense fallback={<SkeletonLayout />}>
              <InstallPage />
            </Suspense>
          }
        />
        <Route
          path="/*"
          element={
            hasAccess ? (
              <ParentRoutes
                isEmployee={isEmployee}
                isParent={isParent}
                profile={profile}
              />
            ) : (
              <Navigate to="/error/403/" replace />
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ImpersonationProvider>
      <AppContent />
    </ImpersonationProvider>
  );
}

export default App;
